import type { ReactNode } from 'react';
import { Page403 } from 'framework/statusPages';
import { Loading } from 'framework/ui/mui-builder';
import { useCoreServiceClient } from './api/coreServiceClient';
import ErrorNotice from './components/ErrorNotice';
import useAsyncData from './hooks/useAsyncData';

const FORBIDDEN_STATUSES = [401, 403];

const isForbidden = (error: unknown): boolean => {
  const status = (error as { status?: number } | null)?.status;

  return status !== undefined && FORBIDDEN_STATUSES.includes(status);
};

/**
 * The core API is CTO-only and decides it from the caller's Kernel roles, so the page asks the API itself instead of
 * duplicating the rule: anybody the API refuses sees the 403 page and never gets to the tabs.
 */
const CoreServiceAccessGuard = ({ children }: { children: ReactNode }) => {
  const client = useCoreServiceClient();
  const access = useAsyncData(() => client.getSyncStatus(), [client]);

  if (isForbidden(access.error)) {
    return <Page403 />;
  }

  if (access.error && !access.data) {
    return <ErrorNotice error={access.error} onRetry={access.reload} />;
  }

  if (!access.data) {
    return <Loading isInitialPosition />;
  }

  return <>{children}</>;
};

export default CoreServiceAccessGuard;
